<template>
	<div class="widgetImage" ref="widgetRef" @click="selectCurWidget">
		<img class="image" ref="imageRef" @load="handleLoad" />
		<div class="mask" ref="maskRef" />
		<div class="border" ref="borderRef" />
	</div>
</template>

<script lang="ts">
import { defineComponent, inject, onMounted, PropType, reactive, ref, watch } from 'vue';
import { dataKey, ListWidget, Ornt, State, WidgetImage, widgetType } from '../types';
import apportion from '../util/apportion';
import getBase64Image from '../util/getBase64Image';

export default defineComponent({
	props: {
		me: { type: Object as PropType<WidgetImage>, required: true },
		parentType: { type: String, required: true },
	},
	setup(props) {
		const widgetRef = ref<HTMLElement>();
		const imageRef = ref<HTMLImageElement>();
		const maskRef = ref<HTMLElement>();
		const borderRef = ref<HTMLElement>();
		const me = reactive(props.me);
		const parentType = ref(props.parentType);
		const state: State = inject<any>(dataKey.STATE);

		const selectCurWidget = (e: MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			state.curWidget = me;
		};

		let one = true;
		let imgSrc = me.attrs.image;
		const handleLoad = (e: Event) => {
			const target = e.target as HTMLImageElement;
			me.rawImageSize[Ornt.Y] = target.naturalHeight;
			me.rawImageSize[Ornt.X] = target.naturalWidth;
			me.rawImageDate = getBase64Image(imageRef.value as HTMLImageElement);
			if (one || imgSrc !== me.attrs.image) {
				imgSrc = me.attrs.image;
				apportion(state.rootWidget as ListWidget);
				one = false;
			}
		};

		onMounted(() => {
			const rootWidget = state.rootWidget as ListWidget;
			const widget = widgetRef.value as HTMLElement;
			const img = imageRef.value as HTMLImageElement;
			const mask = maskRef.value as HTMLElement;
			const border = borderRef.value as HTMLElement;
			let lastSize = me.attrs.imageSize.width + me.attrs.imageSize.height;
			let lastResizable = me.attrs.resizable;
			let lastContentMode = me.attrs.contentMode;
			watch(
				me,
				value => {
					// image
					img.src = value.attrs.image;

					// imageSize
					const nowSize = me.attrs.imageSize.width + me.attrs.imageSize.height;
					if (lastSize != nowSize) {
						lastSize = nowSize;
						apportion(rootWidget);
					}

					// width、height
					widget.style.width = value.size.width + 'px';
					widget.style.height = value.size.height + 'px';

					// border
					border.style.borderColor = me.attrs.borderColor;
					border.style.borderWidth = me.attrs.borderWidth + 'px';

					// imageOpacity
					widget.style.opacity = me.attrs.imageOpacity + '';

					// tintColor
					mask.style.backgroundColor = me.attrs.tint.color;
					mask.style.opacity = me.attrs.tint.opacity + '';

					// alignImage
					if (parentType.value === widgetType.LIST) {
						widget.style.alignSelf = me.attrs.alignImage;
					} else {
						widget.style.alignSelf = 'flex-start';
					}

					// contentMode;
					if (lastContentMode !== me.attrs.contentMode) {
						lastContentMode = me.attrs.contentMode;
						apportion(rootWidget);
					}
					const rate = me.rawImageSize[Ornt.X] / me.rawImageSize[Ornt.Y];
					const realWidgetWidth = +getComputedStyle(widget).width.slice(0, -2);
					const realWidgetHeight = +getComputedStyle(widget).height.slice(0, -2);
					if (me.attrs.contentMode === 'fill' && me.attrs.imageSize.width + me.attrs.imageSize.height > 0) {
						let maxLen = rate * realWidgetHeight;
						if (maxLen > realWidgetWidth) {
							img.style.width = maxLen + 'px';
							img.style.height = realWidgetHeight + 'px';
						} else {
							maxLen = (1 / rate) * realWidgetWidth;
							img.style.width = realWidgetWidth + 'px';
							img.style.height = maxLen + 'px';
						}
					} else {
						img.style.width = '100%';
						img.style.height = '100%';
					}

					// resizable
					if (lastResizable !== me.attrs.resizable) {
						lastResizable = me.attrs.resizable;
						apportion(rootWidget);
					}
					if (!lastResizable) {
						img.style.width = me.rawImageSize.width + 'px';
						img.style.height = me.rawImageSize.height + 'px';
					} else {
						img.style.width = '100%';
						img.style.height = '100%';
					}

					// cornerRadius
					if (me.attrs.containerRelativeShape) {
						border.style.borderRadius = me.attrs.cornerRadius + 'px';
						img.style.borderRadius = '0px';
					} else {
						border.style.borderRadius = me.attrs.cornerRadius + 'px';
						img.style.borderRadius = me.attrs.cornerRadius + 'px';
					}

					// 图片溢出自身容器的处理
					const overflowWidth =
						+getComputedStyle(img).width.slice(0, -2) - +getComputedStyle(widget).width.slice(0, -2);
					const overflowHeight =
						+getComputedStyle(img).height.slice(0, -2) - +getComputedStyle(widget).height.slice(0, -2);
					img.style.left = -(overflowWidth / 2) + 'px';
					img.style.top = -(overflowHeight / 2) + 'px';
				},
				{ deep: true, immediate: true }
			);
		});

		return { widgetRef, imageRef, maskRef, borderRef, selectCurWidget, handleLoad };
	},
});
</script>

<style scoped>
.widgetImage {
	position: relative;
}
.image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
}
.mask {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}
.border {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	border-style: solid;
}
</style>
